import styled from "styled-components";
import tw from "twin.macro";

// Styled Components
export const FilterOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4`}
  backdrop-filter: blur(4px);
`;

export const FilterModal = styled.div`
  ${tw`bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-screen overflow-hidden flex flex-col`}
  max-height: 85vh;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

export const FilterHeader = styled.div`
  ${tw`flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50`}
`;

export const FilterTitle = styled.div`
  ${tw`flex items-center gap-3`}
  
  h2 {
    ${tw`text-xl font-bold text-gray-900 m-0`}
  }
`;

export const FilterIcon = styled.div`
  ${tw`text-blue-600 p-2 bg-blue-100 rounded-lg`}
`;

export const ActiveFiltersBadge = styled.span`
  ${tw`bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full px-3 py-1 text-xs font-bold min-w-6 text-center shadow-lg`}
`;

export const CloseButton = styled.button`
  ${tw`p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white transition-all border-none bg-transparent cursor-pointer shadow-sm`}
  
  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }
`;

export const FilterContent = styled.div`
  ${tw`flex-1 overflow-y-auto p-6 space-y-6`}
`;

export const FilterSection = styled.div`
  ${tw`bg-gray-50 rounded-xl p-5 border border-gray-100`}
`;

export const SectionHeader = styled.div`
  ${tw`flex items-center gap-3 mb-4`}
`;

export const SectionIcon = styled.div`
  ${tw`text-gray-600 p-2 bg-white rounded-lg shadow-sm`}
`;

export const SectionTitle = styled.h3`
  ${tw`text-lg font-bold text-gray-800 m-0`}
`;

export const ModernDateModeSelector = styled.div`
  ${tw`flex gap-2 mb-4 p-1 bg-white rounded-lg border border-gray-200`}
`;

export const DateModeButton = styled.button`
  ${tw`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium text-sm transition-all border-none cursor-pointer`}
  
  ${props => props.$active 
    ? tw`bg-blue-600 text-white shadow-md` 
    : tw`bg-transparent text-gray-600 hover:bg-gray-100`
  }
  
  svg {
    ${tw`w-4 h-4`}
  }
`;

export const InputRow = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4`}
`;

export const ModernInputGroup = styled.div`
  ${tw`flex flex-col gap-2`}
`;

export const InputLabel = styled.label`
  ${tw`font-semibold text-gray-700 text-sm`}
`;

export const ModernInput = styled.input`
  ${tw`px-4 py-3 border border-gray-200 rounded-xl text-sm transition-all bg-white shadow-sm`}
  
  &:focus {
    ${tw`outline-none border-blue-500 ring-2 ring-blue-100 shadow-md`}
  }
  
  ${props => props.$hasError && tw`border-red-400 ring-2 ring-red-100`}
  ${props => props.$hasPrefix && tw`pl-12`}
  
  &[type="number"] {
    -moz-appearance: textfield;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
  
  &::placeholder {
    ${tw`text-gray-400`}
  }
`;

export const ModernSelect = styled.select`
  ${tw`px-4 py-3 border border-gray-200 rounded-xl text-sm transition-all bg-white shadow-sm cursor-pointer`}
  
  &:focus {
    ${tw`outline-none border-blue-500 ring-2 ring-blue-100 shadow-md`}
  }
`;

export const AmountInputWrapper = styled.div`
  ${tw`relative`}
`;

export const CurrencyPrefix = styled.span`
  ${tw`absolute left-4 top-1/2 transform -translate-y-1/2 text-sm font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded-md`}
`;

export const ErrorMessage = styled.span`
  ${tw`text-red-500 text-xs font-semibold mt-1 flex items-center gap-1`}
  
  &:before {
    content: "⚠";
    ${tw`text-red-400`}
  }
`;

export const FilterFooter = styled.div`
  ${tw`flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50`}
`;

export const ActionButtons = styled.div`
  ${tw`flex gap-3`}
`;

export const BaseButton = styled.button`
  ${tw`px-6 py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all border-none flex items-center gap-2 shadow-sm`}
  
  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }
`;

export const ClearButton = styled(BaseButton)`
  ${tw`bg-gray-200 text-gray-700 hover:bg-gray-300`}
`;

export const CancelButton = styled(BaseButton)`
  ${tw`bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-md`}
`;

export const ApplyButton = styled(BaseButton)`
  ${tw`bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg`}
`;

export const LoadingSpinner = styled.div`
  ${tw`w-4 h-4 border-2 border-white border-t-transparent rounded-full`}
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;